import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
} from "@material-ui/core";
import { DateRange } from "@material-ui/icons";
import {
  LmsConnectionsReportFilterInput,
  useGetLmsConnectionsReportQuery,
} from "../../../generated/graphql";
import { API } from "../../../core/axios/client";
import { downloadFile } from "../../../helpers/downloadFile.helper";
import { CustomTablePagination } from "../../shared/CustomTablePagination";
import { DateRangePicker } from "../../shared/DateRangePicker";
import { initialDateRange } from "../../shared/DateRangePicker/DateRangePicker";
import { ErrorMessageSnackbar } from "../../shared/ErrorMessageSnackbar";
import { LoadingProgress } from "../../shared/LoadingProgress";
import {
  ButtonPlus,
  DatePickerButton,
  LoadingBox,
} from "../../shared/Style/Style";
import { HeadColumns } from "./HeadColumns";
import { LmsFilter } from "./LmsFilter";
import { ReportMessageRow, ReportRows } from "./ReportRows";
import { ReportSearch } from "./ReportSearch";
import {
  CSV_DOWNLOAD_ERROR,
  CSV_EXPORT_URL,
  PERIOD_LABEL,
  REPORT_LOAD_ERROR,
  ROWS_PER_PAGE,
  SCHOOLS_COUNT_LABEL,
} from "./constants";
import { FiltersBar, ReportSummary } from "./styles";
import { ReportFilterState } from "./types";
import { describePeriod, exportFileName } from "./utils";

const initialFilter: ReportFilterState = {
  integrationIds: [],
  dateRange: initialDateRange,
  searchKey: null,
};

// The filter as both the GraphQL query and the CSV endpoint understand it (paging added separately).
const toFilterInput = ({
  integrationIds,
  dateRange,
  searchKey,
}: ReportFilterState): LmsConnectionsReportFilterInput => ({
  integrationIds: integrationIds.length > 0 ? integrationIds : undefined,
  from: dateRange.startDate || undefined,
  to: dateRange.endDate || undefined,
  searchKey: searchKey || undefined,
});

// AT2-20374: which schools connected to which LMS, and when. Rows come from lms_school_mapping
// stamps (first / last successful launch), so only connections that actually worked are listed;
// the period narrows by the first launch, on Kyiv calendar days.
export const ReportsTab = () => {
  const [filter, setFilter] = useState<ReportFilterState>(initialFilter);
  const [page, setPage] = useState(0);
  const [isDateRangePickerOpen, setDateRangePickerOpen] = useState(false);
  const [isCsvLoading, setCsvLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { startDate, endDate } = filter.dateRange;
  const isDatePickerActive = !!startDate || !!endDate;

  const { data, loading, error } = useGetLmsConnectionsReportQuery({
    variables: {
      filter: {
        ...toFilterInput(filter),
        limit: ROWS_PER_PAGE,
        skip: page * ROWS_PER_PAGE,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (error) {
      setErrorMessage(REPORT_LOAD_ERROR);
    }
  }, [error]);

  const rows = data?.getLmsConnectionsReport?.rows || [];
  const total = data?.getLmsConnectionsReport?.total || 0;
  const schoolsCount = data?.getLmsConnectionsReport?.schoolsCount || 0;
  const isReportBroken = !!error && !data;

  // Any filter change starts from the first page — in the same handler, so the query never fires
  // with a new filter and a stale skip.
  const updateFilter = (patch: Partial<ReportFilterState>) => {
    setFilter((prev) => ({ ...prev, ...patch }));
    setPage(0);
  };

  const downloadCsv = async () => {
    try {
      setCsvLoading(true);

      const response = await API.post(CSV_EXPORT_URL, toFilterInput(filter), {
        responseType: "blob",
      });

      downloadFile(response, exportFileName(filter.dateRange));
    } catch (e) {
      console.error(e);
      setErrorMessage(CSV_DOWNLOAD_ERROR);
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <>
      <FiltersBar>
        <LmsFilter
          selectedIds={filter.integrationIds}
          onChange={(integrationIds) => updateFilter({ integrationIds })}
        />

        <DatePickerButton
          onClick={() => setDateRangePickerOpen(true)}
          isActive={isDatePickerActive}
        >
          <DateRange />
          Період
        </DatePickerButton>

        <ReportSearch
          searchKey={filter.searchKey}
          onChange={(searchKey) => updateFilter({ searchKey })}
        />

        <Box marginLeft="auto">
          {!isCsvLoading ? (
            <ButtonPlus
              width={180}
              variant="contained"
              noPlus
              disabled={loading || total === 0}
              onClick={downloadCsv}
            >
              Завантажити
            </ButtonPlus>
          ) : (
            <LoadingBox width={180}>
              <CircularProgress color="inherit" />
            </LoadingBox>
          )}
        </Box>
      </FiltersBar>

      {/* The picker closes after saving, so the chosen period has to stay visible somewhere. */}
      <ReportSummary>
        <span>
          {PERIOD_LABEL}: <b>{describePeriod(filter.dateRange)}</b>
        </span>
        <span>
          {SCHOOLS_COUNT_LABEL}: <b>{isReportBroken ? "—" : schoolsCount}</b>
        </span>
      </ReportSummary>

      {loading && !data ? (
        <LoadingProgress />
      ) : (
        <Paper>
          <TableContainer>
            <Table stickyHeader>
              <HeadColumns />
              <TableBody>
                {isReportBroken ? (
                  <ReportMessageRow text={REPORT_LOAD_ERROR} />
                ) : (
                  <ReportRows rows={rows} />
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {total > ROWS_PER_PAGE ? (
            <CustomTablePagination
              rowsPerPageOptions={[ROWS_PER_PAGE]}
              count={total}
              rowsPerPage={ROWS_PER_PAGE}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={() => undefined}
            />
          ) : null}
        </Paper>
      )}

      <DateRangePicker
        setDateRange={(dateRange) => updateFilter({ dateRange })}
        open={isDateRangePickerOpen}
        onClose={() => setDateRangePickerOpen(false)}
      />

      <ErrorMessageSnackbar
        open={errorMessage !== null}
        message={errorMessage ?? ""}
        closeHandler={() => setErrorMessage(null)}
      />
    </>
  );
};
